import styled, { css } from 'styled-components'
import PageLayout from 'layouts/PageLayout'
import { breakpoint } from '@sketch/global-styles'

/**
 * TYPES
 */
interface SidebarProps {
  isSidebarOpen?: boolean
}

interface ContentContainerProps {
  isSidebarOpen?: boolean
}

/**
 * COMPONENTS
 */
export const FullPageLayout = styled(PageLayout)`
  width: 100%;
  height: 100%;
  margin: 0;
`

export const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;

  /* Force the page to take the maximum of PageLayout */
  flex: 1;
`

// Left sidebar to show the main navigation:
// We show the sidebar for mobile (hidden by default), for desktop we always
// show the sidebar
export const Sidebar = styled.aside<SidebarProps>(
  ({ theme, isSidebarOpen }) => css`
    display: flex;

    width: ${theme.sidebar.leftWidth}px;

    position: absolute;
    top: 0;
    bottom: 0;
    z-index: ${theme.zIndex[5]}; /* bigger z-index than header */

    background-color: ${theme.colors.background.secondary.B};
    border-right: 1px solid ${theme.colors.border.A};
    border-top-width: 0;
    border-bottom-width: 0;

    transition-timing-function: ease-in-out;
    transition-duration: 0.3s;
    transition-property: transform, opacity;

    will-change: transform; /* Force hardware acceleration */
    transform: ${isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)'};

    opacity: ${({ isSidebarOpen }: SidebarProps) => (isSidebarOpen ? 1 : 0)};

    /* For bigger than mobile we always show the sidebar */
    ${breakpoint('sm')`
      position: static;
      opacity: 1;
      transform: none;
      transition: none;
      width: ${theme.sidebar.leftWidth}px;
    `}
  `
)

export const Content = styled.div(
  ({ theme }) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    background-color: ${theme.colors.background.secondary.A};
    z-index: ${theme.zIndex[2]};
  `
)

export const ContentContainer = styled.div<ContentContainerProps>(
  ({ theme, isSidebarOpen }) => css`
    position: relative;

    display: flex;
    flex: 1 0 auto;
    flex-direction: column;

    transition-timing-function: ease-in-out;
    transition-duration: 0.3s;
    transition-property: transform, opacity;

    transform: ${isSidebarOpen
      ? `translateX(${theme.sidebar.leftWidth}px)`
      : undefined};

    /* Prevent horizontal scrolling when the sidebar is opened */
    overflow-x: ${isSidebarOpen ? 'hidden' : undefined};

    ${breakpoint('sm')`
      transform: none;
      overflow-x: initial;
    `};
  `
)
